import { ISOCurrency, LEGACY_CURRENCIES } from "./currency";
import type CustomerId from "./state/CustomerId";

export type ClassList = Record<string, boolean | undefined> | undefined;

export type WithClassList<T> = { classList?: ClassList } & T;

export type Nullish<T> = T | undefined | null;

export type ValueOf<ObjectType> = ObjectType[keyof ObjectType];

export type HexColor = `#${string}`;

export type ThemeName = "Default" | "Blue" | "Green" | "Purple" | "Red" | "Brown" | "Pink" | "Gray";
export type ThemeId =
  | "5ee1f55f54e4e995cc284b74"
  | "5ee1f55f54e4e995cc284b75"
  | "5ee1f55f54e4e995cc284b77"
  | "5ee1f55f54e4e995cc284b78"
  | "5ee1f55f54e4e995cc284b79"
  | "5ee1f55f54e4e995cc284b81"
  | "5ee1f55f54e4e995cc284b82"
  | "5ee1f55f54e4e995cc284b83";
export type Theme = {
  id: ThemeId;
  name: ThemeName;
  color: HexColor;
};
export const themeIconColour = {
  Default: "icon-brand",
  Blue: "icon-theme-blue",
  Green: "icon-theme-green",
  Purple: "icon-theme-purple",
  Red: "icon-theme-red",
  Brown: "icon-theme-orange",
  Pink: "icon-theme-pink",
  Gray: "icon-theme-gray",
} as const;

export const THEMES: Record<ThemeName, Theme> = {
  Default: { id: "5ee1f55f54e4e995cc284b74", name: "Default", color: "#3F5D8B" },
  Green: { id: "5ee1f55f54e4e995cc284b75", name: "Green", color: "#41784C" },
  Purple: { id: "5ee1f55f54e4e995cc284b79", name: "Purple", color: "#6D28D9" },
  Blue: { id: "5ee1f55f54e4e995cc284b77", name: "Blue", color: "#1D4ED8" },
  Brown: { id: "5ee1f55f54e4e995cc284b81", name: "Brown", color: "#994C19" },
  Gray: { id: "5ee1f55f54e4e995cc284b83", name: "Gray", color: "#343D4B" },
  Pink: { id: "5ee1f55f54e4e995cc284b82", name: "Pink", color: "#BE185D" },
  Red: { id: "5ee1f55f54e4e995cc284b78", name: "Red", color: "#B91C1C" },
};

export const FIELD_TYPES = [
  "header",
  "image",
  "help",
  "label",
  "html",
  "text",
  "textArea",
  "readOnlyText",
  "radio",
  "checkbox",
  "lookup",
  "smiley",
  "rating",
  "video",
  "file",
  "photo",
  "drawing",
  "pin",
  "email",
  "phone",
  "signature",
  "date",
  "time",
  "dateTime",
  "timeDifference",
  "stopwatch",
  "search",
  "catalogue",
  "barcode",
  "iban",
  "instructionHtml",
  "licensePlate",
  "number",
  "slider",
  "calculation",
  "price",
  "location",
  "currentLocation",
  "address",
  "subform",
  "unknown",
] as const;

export type FieldType = (typeof FIELD_TYPES)[number];

export type WidgetUid = `${string}:${string}:${number}`;

export const CATEGORIES = [
  "choices",
  "dateTime",
  "layout",
  "location",
  "media",
  "number",
  "personalInfo",
  "searchItems",
  "subform",
  "textFields",
  "unknown",
] as const;

export type Category = (typeof CATEGORIES)[number];

export const OPERATORS = ["addition", "subtract", "multiply", "divide", "average", "minimum", "maximum"] as const;

export type Operator = (typeof OPERATORS)[number];

export const DECIMAL_MARKS = ["comma", "period"];

export type DecimalMark = (typeof DECIMAL_MARKS)[number];

export const CURRENCIES = [...Object.keys(ISOCurrency), ...LEGACY_CURRENCIES];

export type Currency = (typeof CURRENCIES)[number];

export const HEADINGS = ["h1", "h2", "h3", "h4", "h5", "h6"] as const;

export type Heading = (typeof HEADINGS)[number];

export const FILE_TYPES = ["images", "video", "audio", "documents"] as const;

export type FileType = (typeof FILE_TYPES)[number];

export const PHOTO_QUALITIES = ["low", "medium", "high", "best"] as const;

export type PhotoQuality = (typeof PHOTO_QUALITIES)[number];

export type DeepPartial<T> = T extends object
  ? {
      [P in keyof T]?: DeepPartial<T[P]>;
    }
  : T;

export type Stringifiable = {
  toString(): string;
};

export enum Scope {
  ROOT = "ROOT",
  GLOBAL = "GLOBAL",
}

export type CustomerInfo = {
  customerId: CustomerId;
  name: string;
};

export type Usage = {
  total: number;
  data: {
    x: Date;
    y: number;
  }[];
};

export type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
export type RequiredBy<T, K extends keyof T> = Omit<T, K> & { [P in K]-?: NonNullable<T[P]> };

export const STATUSES = ["upgrade", "informative", "warning", "danger"] as const;
export type Status = (typeof STATUSES)[number];
